/**
 * Cache Warming Utilities
 *
 * This module provides functions to prefetch and warm up the cache for critical data.
 * Using these functions can significantly improve perceived performance by loading
 * data in the background before it's needed.
 */

import { QueryClient } from "react-query";
import { endpoints } from "../api/constants/endpoints";
import fetcher from "./fetchWrapper";

// Store a reference to the query client
let queryClientRef: QueryClient | null = null;

// Function to set the query client reference from outside this module
export const setQueryClientForCacheWarming = (client: QueryClient) => {
  queryClientRef = client;
};

/**
 * Warms the cache for services data
 * Used for prefetching data that will be needed on the Services page
 * Enhanced to support the consolidated service data approach
 */
export const warmServicesCache = async (
  accountId: string,
  accountType: string = "RESIDENTIAL",
  userId: string = ""
) => {
  // Skip if accountId is missing
  if (!accountId) {
    console.warn("Cannot warm services cache: missing accountId");
    return null;
  }

  try {
    // Don't log in production to avoid console clutter
    if (process.env.REACT_ENV === "development") {
      console.log("Warming services cache for account:", accountId);
    }

    // Service data parameters - this is our single source of truth for all service data
    const servicesLoading =
      "Services.Provider.Contacts.Location,Services.Plans";
    const servicesFilter =
      "Services.display_type eq PUBLIC::" +
      "Services.status eq ACTIVE::" +
      "Services.Provider.status eq ACTIVE::" +
      "Services.Plans.status eq ACTIVE::" +
      `Services.account_types like "${accountType}"`;

    // No more separate minimal service data - we transform the full data as needed

    // Set up subscription query parameters
    const subscriptionLoading =
      "Serviceplan.Service.Provider.Contacts," +
      "Serviceplan.Service.Servicetype," +
      "Portinterface.Device.Physicallocation";

    // Pre-fetch data in parallel - focus on the most important data
    const [serviceTypesResponse, subscriptionsResponse] = await Promise.all([
      // Full service data with plans
      fetcher(
        `${endpoints.getServiceTypes}?loading=${servicesLoading}&filter=${servicesFilter}`
      ),
      // Subscriptions
      fetcher(
        `${endpoints.account}/${accountId}/subscription?loading=${subscriptionLoading}`
      ),
    ]);

    const serviceTypes = await serviceTypesResponse.json();
    const subscriptions = await subscriptionsResponse.json();

    // Make sure we have a query client reference
    if (!queryClientRef) {
      console.warn(
        "Query client not set for cache warming - service types data won't be cached"
      );
      return { serviceTypes, subscriptions };
    }

    // Update all relevant query caches to prevent duplicate requests

    // SINGLE SOURCE OF TRUTH - this is the primary key for the detailed service data
    queryClientRef.setQueryData(
      ["servicetypes", "full", accountType, true, true, true],
      serviceTypes
    );

    // Subscriptions data
    queryClientRef.setQueryData(
      ["subscriptions", userId || "userId", accountId],
      subscriptions
    );

    // Don't log in production to avoid console clutter
    if (process.env.REACT_ENV === "development") {
      console.log("Cache warming complete");
    }

    return { serviceTypes, subscriptions };
  } catch (error) {
    console.error("Error warming services cache:", error);
    return null;
  }
};

/**
 * Warms the cache for common API data used across multiple pages
 * Used for prefetching global data on application startup
 */
export const warmCommonDataCache = async () => {
  try {
    // Don't log in production to avoid console clutter
    if (process.env.REACT_ENV === "development") {
      console.log("Warming common data cache");
    }

    // Prefetch common API endpoints
    const [enabledFeaturesResponse, variablesResponse] = await Promise.all([
      fetcher(endpoints.getEnabledFeatures),
      fetcher(endpoints.getSystemVariables),
    ]);

    const enabledFeatures = await enabledFeaturesResponse.json();
    const variables = await variablesResponse.json();

    // Make sure we have a query client reference
    if (!queryClientRef) {
      console.warn(
        "Query client not set for cache warming - common data won't be cached"
      );
      return { enabledFeatures, variables };
    }

    // Update query cache
    queryClientRef.setQueryData(["enabledfeatures"], enabledFeatures);
    queryClientRef.setQueryData(["variables"], variables);

    // Don't log in production to avoid console clutter
    if (process.env.REACT_ENV === "development") {
      console.log("Common data cache warming complete");
    }

    return { enabledFeatures, variables };
  } catch (error) {
    console.error("Error warming common data cache:", error);
    return null;
  }
};
